import React, { useEffect, useRef, useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import AxiosUtil from '../../Utils/AxiosUtil';
import useAuth from '../../Hooks/useAuth';
import { CoreBrand, CoreTenant, CoreType, CoreField, CoreDataPoint, CoreDataPointDataType } from '../Shared/CoreComponents';
import AHRBuilderSelections from '../Shared/AHRBuilderSelections';
import AHRBCoreTypeRootSelection from '../Shared/AHRBCoreTypeRootSelection';
import { invalidateReportMenu } from '../Shared/ReportMenuQuery';
import Administration from '../../Constants/Endpoints/Administration';
import GetCoreBrandAdminModelsRequest from '../../Interfaces/Requests/Administration/GetCoreBrandAdminModelsRequest';
import GetCoreBrandAdminModelsResponse from '../../Interfaces/Responses/Administration/GetCoreBrandAdminModelResponse';
import GetCoreTenantAdminModelsRequest from '../../Interfaces/Requests/Administration/GetCoreTenantAdminModelsRequest';
import GetCoreTenantAdminModelsResponse from '../../Interfaces/Responses/Administration/GetCoreTenantAdminModelResponse';
import GetCoreTypeAdminModelsRequest from '../../Interfaces/Requests/Administration/GetCoreTypeAdminModelsRequest';
import GetCoreTypeAdminModelsResponse from '../../Interfaces/Responses/Administration/GetCoreTypeAdminModelResponse';
import GetCoreFieldAdminModelsRequest from '../../Interfaces/Requests/Administration/GetCoreFieldAdminModelsRequest';
import GetCoreFieldAdminModelsResponse from '../../Interfaces/Responses/Administration/GetCoreFieldAdminModelResponse';
import AdHocReportDefinitionListRequestModel from '../../Interfaces/Requests/Reports/AdHocReportDefinitionListRequestModel';
import AdHocReportDefinitionListResponseModel from '../../Interfaces/Responses/Reports/AdHocReportDefinitionListResponseModel';
import AdHocReportDefinitionSetRequestModel from '../../Interfaces/Requests/Reports/AdHocReportDefinitionSetRequestModel';
import AdHocReportDefinitionSetResponseModel from '../../Interfaces/Responses/Reports/AdHocReportDefinitionSetResponseModel';
import AdHocReportDefinitionGetRequestModel from '../../Interfaces/Requests/Reports/AdHocReportDefinitionGetRequestModel';
import AdHocReportDefinitionGetResponseModel from '../../Interfaces/Responses/Reports/AdHocReportDefinitionGetResponseModel';
import { AHRBDataPointCustomizationMap, AHRBDataPointAggregateMap, AHRBDataPointFixedWhereMap, AHRBDataPointFixedWhereOperator } from '../../Interfaces/Reports/AHRBDataPointConfigurationModels';
import AdHocReportOrderByModel, { DynamicOrderByDirection } from '../../Interfaces/Requests/Reports/AdHocReportOrderByModel';
import AdHocReportAggregateModel, { DynamicAggregateType } from '../../Interfaces/Requests/Reports/AdHocReportAggregateModel';
import AdHocReportFixedWhereModel, { AdHocReportFixedWhereOperator } from '../../Interfaces/Requests/Reports/AdHocReportFixedWhereModel';
import AdHocReportVariableWhereModel from '../../Interfaces/Requests/Reports/AdHocReportVariableWhereModel';
import AdHocReportGroupByModel from '../../Interfaces/Requests/Reports/AdHocReportGroupByModel';
import AdHocReportFormatModel from '../../Interfaces/Requests/Reports/AdHocReportFormatModel';
import {
    Alert as ServaAlert,
    Button,
    Card,
    Col,
    Divider,
    Form,
    Input,
    Modal as ServaModal,
    Option,
    Row,
    Select,
    Space,
    Spin,
    Text,
    Title,
} from '@servahealth/serva-ui-kit';

type AdHocReportBuilderProps = {};

const AdHocReportBuilder: React.FC<AdHocReportBuilderProps> = () => {
    const { user } = useAuth();
    const [formAdHocReportBuilder] = Form.useForm();

    const [coreBrands, setCoreBrands] = useState<CoreBrand[]>([]);
    const [coreTenants, setCoreTenants] = useState<CoreTenant[]>([]);
    const [coreTypes, setCoreTypes] = useState<CoreType[]>([]);
    const [coreFields, setCoreFields] = useState<CoreField[]>([]);

    const [ahExistingReports, setAHExistingReports] = useState<AdHocReportDefinitionListResponseModel[]>([]);
    const [ahSelectedReportID, setAHSelectedReportID] = useState(0);
    const [ahReportBuilderSessionKey, setAHReportBuilderSessionKey] = useState(0);

    const [ahSelectedCoreTypes, setAHSelectedCoreTypes] = useState<CoreType[]>([]);
    const [ahSelectedCoreDataPoints, setAHSelectedCoreDataPoints] = useState<CoreDataPoint[]>([]);
    const [ahSelectedOrderByDataPointID, setAHSelectedOrderByDataPointID] = useState<number | undefined>();
    const [ahDataPointCustomizations, setAHDataPointCustomizations] = useState<AHRBDataPointCustomizationMap>({});
    const [ahDataPointAggregates, setAHDataPointAggregates] = useState<AHRBDataPointAggregateMap>({});
    const [ahDataPointFixedWheres, setAHDataPointFixedWheres] = useState<AHRBDataPointFixedWhereMap>({});
    const [ahDataPointVariableWheres, setAHDataPointVariableWheres] = useState<AdHocReportVariableWhereModel[]>([]);

    const queryClient = useQueryClient();

    const ahSelectedCoreTypeRootDataRef = useRef<CoreType | undefined>();

    const [ahSelectedCoreTenantID, setAHSelectedCoreTenantID] = useState(0);
    const [ahSelectedCoreTenantName, setAHSelectedCoreTenantName] = useState('');
    const [ahSelectedCoreBrandID, setAHSelectedCoreBrandID] = useState(0);
    const [ahSelectedCoreBrandName, setAHSelectedCoreBrandName] = useState('');

    const [ahReportCoreTypeRootDataOpen, setAHReportCoreTypeRootDataOpen] = useState(false);

    const [ahPendingReportGroupName, setAHPendingReportGroupName] = useState('');
    const [ahPendingReportName, setAHPendingReportName] = useState('');

    const [currentError, setCurrentError] = useState('');

    useEffect(() => {
        getCoreBrands(false);
        getCoreTenants(false);
        getCoreTypes(false);
        getCoreFields(false);
        
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const validateAHDetailsData = (rule: any, value: any) => {
        if (rule.field === 'ahDetailsTenantName') {
            const newAHTenantDetailsName = formAdHocReportBuilder.getFieldValue('ahDetailsTenantName');

            return (!newAHTenantDetailsName || newAHTenantDetailsName.length === 0) ? Promise.reject() : Promise.resolve();
        }

        if (rule.field === 'ahDetailsSelectedReport' || rule.field === 'ahDetailsReportName' || rule.field === 'ahDetailsReportGroupName') {
            const selectedReportID = Number(formAdHocReportBuilder.getFieldValue('ahDetailsSelectedReport') ?? 0);
            const reportName = formAdHocReportBuilder.getFieldValue('ahDetailsReportName');
            const reportGroupName = formAdHocReportBuilder.getFieldValue('ahDetailsReportGroupName');

            const isExistingReport = selectedReportID > 0;

            const hasNewReportDetails =
                selectedReportID === 0 &&
                typeof reportName === 'string' &&
                reportName.trim().length > 0 &&
                typeof reportGroupName === 'string' &&
                reportGroupName.trim().length > 0;

            return isExistingReport || hasNewReportDetails ? Promise.resolve() : Promise.reject();
        }

        return Promise.reject();
    };

    const { mutate: getCoreBrands, isLoading: isGetCoreBrandsLoading, isError: isGetCoreBrandsError, error: getCoreBrandsError } = useMutation(
        async (showDisabled: boolean) => {
            try {
                const url = `${process.env.REACT_APP_API_ROOT}${Administration.GET_CORE_BRAND_ADMIN_MODELS}`;
                const requestData: GetCoreBrandAdminModelsRequest = { gcba_includeDeleted: showDisabled };
                const responseData = AxiosUtil.get<GetCoreBrandAdminModelsResponse[], any>(url, requestData);
                return responseData;
            } catch (err) {
                setCurrentError('GET_CORE_BRAND_ADMIN_MODELS: [Get Core Brand Admin Models AUD] Internal Error: ' + err);
                console.error('GET_CORE_BRAND_ADMIN_MODELS: [Get Core Brand Admin Models AUD] Internal Error: ', err);
                return Promise.reject();
            }
        },
        {
            onError: (err: any) => {
                setCurrentError('GET_CORE_BRAND_ADMIN_MODELS: [Get Core Brand Admin Models AUD] Internal Error: ' + err);
                console.error('GET_CORE_BRAND_ADMIN_MODELS: [Get Core Brand Admin Models AUD] Internal Error: ', err);
            },
            onSuccess: (response: GetCoreBrandAdminModelsResponse[]) => {
                // ---- DEBUG
                // console.log('**** getCoreBrands() response ****');
                // console.log(response);
                // ---- END DEBUG

                if (response) {
                    let coreBrands: CoreBrand[] = response?.map(
                        (eachElement) => {
                            return {
                                id: eachElement.id,
                                idTenant: eachElement.idTenant,
                                name: eachElement.name,
                                guid: eachElement.guid,
                                isDeleted: eachElement.isDeleted,
                                isDisabled: eachElement.isDisabled,
                            };
                        }
                    ).sort((a, b) => a.name.localeCompare(b.name));
                    setCoreBrands(coreBrands);
                }
            }
        }
    );

    const { mutate: getCoreTenants, isLoading: isGetCoreTenantsLoading, isError: isGetCoreTenantsError, error: getCoreTenantsError } = useMutation(
        async (showDisabled: boolean) => {
            try {
                const url = `${process.env.REACT_APP_API_ROOT}${Administration.GET_CORE_TENANT_ADMIN_MODELS}`;
                const requestData: GetCoreTenantAdminModelsRequest = { gcta_includeDeleted: showDisabled };
                const responseData = AxiosUtil.get<GetCoreTenantAdminModelsResponse[], any>(url, requestData);
                return responseData;
            } catch (err) {
                setCurrentError('GET_CORE_TENANT_ADMIN_MODELS: [Get Core Tenant Admin Models AUD] Internal Error: ' + err);
                console.error('GET_CORE_TENANT_ADMIN_MODELS: [Get Core Tenant Admin Models AUD] Internal Error: ', err);
                return Promise.reject();
            }
        },
        {
            onError: (err: any) => {
                setCurrentError('GET_CORE_TENANT_ADMIN_MODELS: [Get Core Tenant Admin Models AUD] Internal Error: ' + err);
                console.error('GET_CORE_TENANT_ADMIN_MODELS: [Get Core Tenant Admin Models AUD] Internal Error: ', err);
            },
            onSuccess: (response: GetCoreTenantAdminModelsResponse[]) => {
                // ---- DEBUG
                // console.log('**** getCoreTenants() response ****');
                // console.log(response);
                // ---- END DEBUG

                if (response) {
                    let coreTenants: CoreTenant[] = response?.map(
                        (eachElement) => {
                            return {
                                id: eachElement.id,
                                name: eachElement.name,
                                dbName: eachElement.dbName,
                                guid: eachElement.guid,
                                isDeleted: eachElement.isDeleted,
                                isDisabled: eachElement.isDisabled,
                            };
                        }
                    ).sort((a, b) => a.name.localeCompare(b.name));
                    setCoreTenants(coreTenants);
                }
            }
        }
    );

    const { mutate: getCoreTypes, isLoading: isGetCoreTypesLoading, isError: isGetCoreTypesError, error: getCoreTypesError } = useMutation(
        async (showDisabled: boolean) => {
            try {
                const url = `${process.env.REACT_APP_API_ROOT}${Administration.GET_CORE_TYPE_ADMIN_MODELS}`;
                const requestData: GetCoreTypeAdminModelsRequest = { gcta_includeDeleted: showDisabled };
                const responseData = AxiosUtil.get<GetCoreTypeAdminModelsResponse[], any>(url, requestData);
                return responseData;
            } catch (err) {
                setCurrentError('GET_CORE_TYPE_ADMIN_MODELS: [Get Core Type Admin Models AUD] Internal Error: ' + err);
                console.error('GET_CORE_TYPE_ADMIN_MODELS: [Get Core Type Admin Models AUD] Internal Error: ', err);
                return Promise.reject();
            }
        },
        {
            onError: (err: any) => {
                setCurrentError('GET_CORE_TYPE_ADMIN_MODELS: [Get Core Type Admin Models AUD] Internal Error: ' + err);
                console.error('GET_CORE_TYPE_ADMIN_MODELS: [Get Core Type Admin Models AUD] Internal Error: ', err);
            },
            onSuccess: (response: GetCoreTypeAdminModelsResponse[]) => {
                // ---- DEBUG
                // console.log('**** getCoreTypes response ****');
                // console.log(response);
                // ---- END DEBUG

                if (response) {
                    let coreTypes: CoreType[] = response?.map(
                        (eachElement) => {
                            return {
                                id: eachElement.id,
                                idBrand: eachElement.idBrand,
                                name: eachElement.name,
                                idFieldExternID: eachElement.idFieldExternID,
                                idParentType: eachElement.idParentType,
                                guid: eachElement.guid,
                                dbName: eachElement.dbName,
                                dbFrom: eachElement.dbFrom,
                                isDeleted: eachElement.isDeleted,
                                guidParent: eachElement.guidParent,
                                isDisabled: eachElement.isDisabled,
                            };
                        }
                    ).sort((a, b) => a.name.localeCompare(b.name));
                    setCoreTypes(coreTypes);
                    // console.log(coreTypes);
                }
            }
        }
    );

    const { mutate: getCoreFields, isLoading: isGetCoreFieldsLoading, isError: isGetCoreFieldsError, error: getCoreFieldsError } = useMutation(
        async (showDisabled: boolean) => {
            try {
                const url = `${process.env.REACT_APP_API_ROOT}${Administration.GET_CORE_FIELD_ADMIN_MODELS}`;
                const requestData: GetCoreFieldAdminModelsRequest = { gcfa_includeDeleted: showDisabled };
                const responseData = AxiosUtil.get<GetCoreFieldAdminModelsResponse[], any>(url, requestData);
                return responseData;
            } catch (err) {
                setCurrentError('GET_CORE_FIELD_ADMIN_MODELS: [Get Core Field Admin Models AUD] Internal Error: ' + err);
                console.error('GET_CORE_FIELD_ADMIN_MODELS: [Get Core Field Admin Models AUD] Internal Error: ', err);
                return Promise.reject();
            }
        },
        {
            onError: (err: any) => {
                setCurrentError('GET_CORE_FIELD_ADMIN_MODELS: [Get Core Field Admin Models AUD] Internal Error: ' + err);
                console.error('GET_CORE_FIELD_ADMIN_MODELS: [Get Core Field Admin Models AUD] Internal Error: ', err);
            },
            onSuccess: (response: GetCoreFieldAdminModelsResponse[]) => {
                // ---- DEBUG
                // console.log('**** getCoreFields response ****');
                // console.log(response);
                // ---- END DEBUG

                if (response) {
                    let coreFields: CoreField[] = response?.map(
                        (eachElement) => {
                            return {
                                id: eachElement.id,
                                idBrand: eachElement.idBrand,
                                name: eachElement.name,
                                dataType: eachElement.dataType,
                                idType: eachElement.idType,
                                isPHI: eachElement.isPHI,
                                idLookupType: eachElement.idLookupType,
                                guid: eachElement.guid,
                                dbName: eachElement.dbName,
                                isDeleted: eachElement.isDeleted,
                                isDisabled: eachElement.isDisabled,
                            };
                        }
                    ).sort((a, b) => a.name.localeCompare(b.name));
                    setCoreFields(coreFields);
                }
            }
        }
    );

    const mapAggregateStringToDynamicAggregateType = (aggregate: string): DynamicAggregateType | null => {
        if (aggregate === 'COUNT') {
            return DynamicAggregateType.Count;
        }
        else if (aggregate === 'SUM') {
            return DynamicAggregateType.Sum;
        }
        else if (aggregate === 'AVG') {
            return DynamicAggregateType.Avg;
        }
        else if (aggregate === 'MIN') {
            return DynamicAggregateType.Min;
        }
        else if (aggregate === 'MAX') {
            return DynamicAggregateType.Max;
        }

        return null;
    };

    const whereOperatorMap: Record<Exclude<AHRBDataPointFixedWhereOperator, ''>, AdHocReportFixedWhereOperator> = {
        BETWEEN: AdHocReportFixedWhereOperator.Between,
        CONTAINS: AdHocReportFixedWhereOperator.Contains,
        ENDSWITH: AdHocReportFixedWhereOperator.EndsWith,
        EQUALS: AdHocReportFixedWhereOperator.Equals,
        GREATERTHAN: AdHocReportFixedWhereOperator.GreaterThan,
        GREATERTHANOREQUAL: AdHocReportFixedWhereOperator.GreaterThanOrEqual,
        ISNOTNULL: AdHocReportFixedWhereOperator.IsNotNull,
        ISNULL: AdHocReportFixedWhereOperator.IsNull,
        LESSTHAN: AdHocReportFixedWhereOperator.LessThan,
        LESSTHANOREQUAL: AdHocReportFixedWhereOperator.LessThanOrEqual,
        NOTEQUALS: AdHocReportFixedWhereOperator.NotEquals,
        STARTSWITH: AdHocReportFixedWhereOperator.StartsWith
    };
    
    const fixedWhereOperatorMap = new Map<AdHocReportFixedWhereOperator, Exclude<AHRBDataPointFixedWhereOperator, ''>>(
        Object.entries(whereOperatorMap).map(
            ([whereOperator, fixedWhereOperator]) => [fixedWhereOperator, whereOperator as Exclude<AHRBDataPointFixedWhereOperator, ''>]
        )
    );

    const mapWhereStringToFixedWhereOperator = (where: AHRBDataPointFixedWhereOperator | string): AdHocReportFixedWhereOperator | null => {
        if (where in whereOperatorMap) {
            return whereOperatorMap[where as Exclude<AHRBDataPointFixedWhereOperator, ''>];
        }

        return null;
    };

    const normalizeBetweenWhereValues = (filteredCoreDataPointModel: CoreDataPoint, valueLHS: string, valueRHS: string): { valueLHS: string; valueRHS: string } => {
        if (filteredCoreDataPointModel.dataType === CoreDataPointDataType.Integer
            || filteredCoreDataPointModel.dataType === CoreDataPointDataType.Float
            || filteredCoreDataPointModel.dataType === CoreDataPointDataType.Autonumber) {
            const numericValueLHS = Number(valueLHS);
            const numericValueRHS = Number(valueRHS);

            if (numericValueLHS > numericValueRHS) {
                return {
                    valueLHS: valueRHS,
                    valueRHS: valueLHS
                };
            }
        }
        else if (filteredCoreDataPointModel.dataType === CoreDataPointDataType.DateTime
            || filteredCoreDataPointModel.dataType === CoreDataPointDataType.Date) {
            const dateValueLHS = new Date(valueLHS);
            const dateValueRHS = new Date(valueRHS);

            if (dateValueLHS > dateValueRHS) {
                return {
                    valueLHS: valueRHS,
                    valueRHS: valueLHS
                };
            }
        }

        return {
            valueLHS: valueLHS,
            valueRHS: valueRHS
        };
    };

    const mapDynamicAggregateTypeToAggregateString = (aggregateType: DynamicAggregateType | string): string => {
        if (aggregateType === DynamicAggregateType.Count || aggregateType === 'Count' || aggregateType === 'COUNT') {
            return 'COUNT';
        }
        else if (aggregateType === DynamicAggregateType.Sum || aggregateType === 'Sum' || aggregateType === 'SUM') {
            return 'SUM';
        }
        else if (aggregateType === DynamicAggregateType.Avg || aggregateType === 'Avg' || aggregateType === 'AVG') {
            return 'AVG';
        }
        else if (aggregateType === DynamicAggregateType.Min || aggregateType === 'Min' || aggregateType === 'MIN') {
            return 'MIN';
        }
        else if (aggregateType === DynamicAggregateType.Max || aggregateType === 'Max' || aggregateType === 'MAX') {
            return 'MAX';
        }

        return '';
    };

    const mapFixedWhereOperatorToWhereString = (where: AdHocReportFixedWhereOperator | string): AHRBDataPointFixedWhereOperator => {
        if (typeof where === 'string' && where in whereOperatorMap) {
            return where as Exclude<AHRBDataPointFixedWhereOperator, ''>;
        }

        return fixedWhereOperatorMap.get(where as AdHocReportFixedWhereOperator) ?? '';
    };

    const buildCustomizationMapFromDefinition = (definition: AdHocReportDefinitionGetResponseModel): AHRBDataPointCustomizationMap => {
        const customizationMap: AHRBDataPointCustomizationMap = {};

        definition.adHocReportFormatData?.forEach(formatData => {
            customizationMap[formatData.formatCoreDataPointModel.id] = formatData.datasetColumnFormatModel;
        });

        return customizationMap;
    };

    const buildAggregateMapFromDefinition = (definition: AdHocReportDefinitionGetResponseModel): AHRBDataPointAggregateMap => {
        const aggregateMap: AHRBDataPointAggregateMap = {};

        definition.adHocReportAggregateData?.forEach(aggregateData => {
            aggregateMap[aggregateData.aggregatedCoreDataPointModel.id] = {
                aggregate: mapDynamicAggregateTypeToAggregateString(aggregateData.aggregateType),
                aggregateColumnAlias: aggregateData.aggregateColumnAlias
            };
        });

        return aggregateMap;
    };

    const buildWhereMapFromDefinition = (definition: AdHocReportDefinitionGetResponseModel): AHRBDataPointFixedWhereMap => {
        const whereMap: AHRBDataPointFixedWhereMap = {};

        definition.adHocReportFixedWhereData?.forEach(whereData => {
            whereMap[whereData.fixedCoreDataPointModel.id] = {
                where: mapFixedWhereOperatorToWhereString(whereData.where),
                valueLHS: whereData.valueLHS ?? '',
                valueRHS: whereData.valueRHS ?? ''
            };
        });

        return whereMap;
    };

    const hydrateAdHocReportBuilder = (definition: AdHocReportDefinitionGetResponseModel, selectedReportID: number) => {
        setAHSelectedCoreBrandID(definition.coreBrandID);
        setAHSelectedCoreBrandName(definition.coreBrandName);

        setAHSelectedCoreTenantID(definition.coreTenantID);
        setAHSelectedCoreTenantName(definition.coreTenantName);

        setAHSelectedReportID(selectedReportID);        

        const brandCoreTypes = coreTypes.filter(coreType =>
            coreType.idBrand === definition.coreBrandID
        );

        setAHSelectedCoreTypes(brandCoreTypes);
        setAHSelectedCoreDataPoints(definition.coreDataPointData ?? []);

        setAHDataPointCustomizations(buildCustomizationMapFromDefinition(definition));
        setAHDataPointAggregates(buildAggregateMapFromDefinition(definition));
        setAHDataPointFixedWheres(buildWhereMapFromDefinition(definition));

        setAHSelectedOrderByDataPointID(definition.adHocReportOrderByData?.[0]?.orderedCoreDataPointModel?.id);

        ahSelectedCoreTypeRootDataRef.current = definition.coreTypeRootData ?? undefined;

        formAdHocReportBuilder.setFieldsValue({
            ahDetailsCoreBrand: definition.coreBrandID,
            ahDetailsTenantName: definition.coreTenantName,
            ahDetailsSelectedReport: selectedReportID,
            ahDetailsReportID: selectedReportID,
            ahDetailsReportName: definition.ahReportName,
            ahDetailsReportGroupName: definition.ahReportGroupName
        });

        setAHReportBuilderSessionKey(currentKey => currentKey + 1);

        setCurrentError('');
    };

    const { mutate: getAHReportList, isLoading: isGetAHReportListLoading, isError: isGetAHReportListError, error: getGetAHReportListError } = useMutation(
        async (coreBrandID: number) => {
            try {
                const url = `${process.env.REACT_APP_API_ROOT}${Administration.ADHOC_REPORT_DEFINITION_LIST_REQUEST}`;

                const requestData: AdHocReportDefinitionListRequestModel = {
                    userID: user?.id ?? 0,
                    coreBrandID: coreBrandID
                };

                const responseData = await AxiosUtil.post<AdHocReportDefinitionListResponseModel[], AdHocReportDefinitionListRequestModel>(url, requestData);

                return responseData;
            } catch (err) {
                const errorMessage = err instanceof Error ? err.message : String(err);

                setCurrentError('GET_AH_REPORT_LIST_REQUEST: [Get AH Report List] Internal Error: ' + errorMessage);
                console.error('GET_AH_REPORT_LIST_REQUEST: [Get AH Report List] Internal Error: ', err);

                return Promise.reject(err);
            }
        },
        {
            onError: (err: any, coreBrandID: number) => {
                setCurrentError('GET_AH_REPORT_LIST_REQUEST: [Get AH Report List] Internal Error: ' + err);
                console.error('GET_AH_REPORT_LIST_REQUEST: [Get AH Report List] Internal Error: ', err);
                showAHReportErrorPopup(
                    'Ad Hoc Report List Load Failed',
                    `Report ${coreBrandID} Was Unable To Be Loaded. FAILED: Contact Application Support`
                );
            },
            onSuccess: (response: AdHocReportDefinitionListResponseModel[]) => {
                const sortedExistingReports = response ? [...response].sort((a, b) =>
                    {
                        const groupCompare = a.ahReportGroupName.localeCompare(b.ahReportGroupName);
                        if (groupCompare !== 0) {
                            return groupCompare;
                        }

                        return a.ahReportName.localeCompare(b.ahReportName);
                    })
                    : [];

                setAHExistingReports(sortedExistingReports);
            },
        }
    );

    const getAHReportSubmitActionText = () => {
        return ahSelectedReportID > 0 ? 'Updated' : 'Added';
    };

    const { mutate: setAHReport, isLoading: isSetAHReportLoading, isError: isSetAHReportError, error: getSetAHReportError } = useMutation(
        async () => {
            try {
                const url = `${process.env.REACT_APP_API_ROOT}${Administration.ADHOC_REPORT_DEFINITION_SET_REQUEST}`;

                const currentAHReportGroupName = formAdHocReportBuilder.getFieldValue('ahDetailsReportGroupName');
                const currentAHReportName = formAdHocReportBuilder.getFieldValue('ahDetailsReportName');
                const currentAHReportID = Number(formAdHocReportBuilder.getFieldValue('ahDetailsSelectedReport') ?? 0);

                const selectedCoreTypeRootData = ahSelectedCoreTypeRootDataRef.current;

                if (!selectedCoreTypeRootData) {
                    throw new Error('Core Type Root is required.');
                }

                const typeIdsInDataPoints = new Set(ahSelectedCoreDataPoints.map(dataPoint => dataPoint.idType));

                const typesInDataPoints = ahSelectedCoreTypes.filter(type => typeIdsInDataPoints.has(type.id));

                const typesInDataPointsWithRoot = typesInDataPoints.some(type => type.id === selectedCoreTypeRootData.id)
                    ?
                    typesInDataPoints
                    :
                    [...typesInDataPoints, selectedCoreTypeRootData];

                const typeIdsInDataPointsWithRoot = new Set(typesInDataPointsWithRoot.map(type => type.id));

                const fieldsInDataPointsWithRoot = coreFields.filter(field => field.idBrand === ahSelectedCoreBrandID && typeIdsInDataPointsWithRoot.has(field.idType));

                const aggregateDataPointIDs = new Set<number>(
                    Object.entries(ahDataPointAggregates)
                        .filter(([dataPointID, aggregateConfiguration]) => aggregateConfiguration.aggregate.trim().length > 0)
                        .map(([dataPointID]) => Number(dataPointID))
                );

                const groupByCoreDataPoints: CoreDataPoint[] = aggregateDataPointIDs.size > 0
                    ?
                    ahSelectedCoreDataPoints.filter(dataPoint => !aggregateDataPointIDs.has(dataPoint.id))
                    :
                    [];

                const groupByDataPointIDs = new Set<number>(groupByCoreDataPoints.map(dataPoint => dataPoint.id));

                const selectedOrderByCoreDataPoint = ahSelectedCoreDataPoints.find(dataPoint => {
                    if (dataPoint.id !== ahSelectedOrderByDataPointID) {
                        return false;
                    }

                    if (aggregateDataPointIDs.size === 0) {
                        return true;
                    }

                    return groupByDataPointIDs.has(dataPoint.id) || aggregateDataPointIDs.has(dataPoint.id);
                });

                const adHocReportOrderByData: AdHocReportOrderByModel[] | null = selectedOrderByCoreDataPoint
                    ?
                    [{
                        coreTypeID: selectedOrderByCoreDataPoint.idType,
                        direction: DynamicOrderByDirection.Ascending,
                        orderedCoreDataPointModel: selectedOrderByCoreDataPoint
                    }]
                    :
                    null;

                const adHocReportGroupByData: AdHocReportGroupByModel[] | null = groupByCoreDataPoints.length > 0
                    ?
                    groupByCoreDataPoints.map(groupByCoreDataPoint => {
                        return {
                            coreTypeID: groupByCoreDataPoint.idType,
                            groupByCoreDataPointModel: groupByCoreDataPoint
                        };
                    })
                    :
                    null;

                const adHocReportFormatDataArray: AdHocReportFormatModel[] = Object.entries(ahDataPointCustomizations)
                    .map(([dataPointID, datasetColumnFormatModel]) => {
                        const formatCoreDataPointModel = ahSelectedCoreDataPoints.find(
                            dataPoint => dataPoint.id === Number(dataPointID)
                        );

                        if (!formatCoreDataPointModel) {
                            return null;
                        }

                        return {
                            coreTypeID: formatCoreDataPointModel.idType,
                            formatCoreDataPointModel: formatCoreDataPointModel,
                            datasetColumnFormatModel: datasetColumnFormatModel
                        };
                    })
                    .filter((formatData): formatData is AdHocReportFormatModel => formatData !== null);

                const adHocReportFormatData: AdHocReportFormatModel[] | null = adHocReportFormatDataArray.length > 0
                    ?
                    adHocReportFormatDataArray
                    :
                    null;

                const adHocReportAggregateDataArray: AdHocReportAggregateModel[] = Object.entries(ahDataPointAggregates)
                    .map(([dataPointID, aggregateConfiguration]) => {
                        const aggregateType = mapAggregateStringToDynamicAggregateType(aggregateConfiguration.aggregate);

                        if (aggregateType === null) {
                            return null;
                        }

                        const aggregatedCoreDataPointModel = ahSelectedCoreDataPoints.find(dataPoint => dataPoint.id === Number(dataPointID));

                        if (!aggregatedCoreDataPointModel) {
                            return null;
                        }

                        const aggregateData: AdHocReportAggregateModel = {
                            coreTypeID: aggregatedCoreDataPointModel.idType,
                            aggregateType: aggregateType,
                            aggregatedCoreDataPointModel: aggregatedCoreDataPointModel,
                            aggregateColumnAlias: aggregateConfiguration.aggregateColumnAlias.trim().length > 0
                                ? aggregateConfiguration.aggregateColumnAlias.trim()
                                : aggregateConfiguration.aggregate + '_' + aggregatedCoreDataPointModel.name.replace(/\s+/g, '_')
                        };

                        return aggregateData;
                    })
                    .filter((aggregateData): aggregateData is AdHocReportAggregateModel => aggregateData !== null);

                const adHocReportAggregateData: AdHocReportAggregateModel[] | null = adHocReportAggregateDataArray.length > 0
                    ?
                    adHocReportAggregateDataArray
                    :
                    null;

                const adHocReportFixedWhereDataArray: AdHocReportFixedWhereModel[] = Object.entries(ahDataPointFixedWheres)
                    .map(([dataPointID, whereConfiguration]) => {
                        const whereStringTrimmed = whereConfiguration.where.trim();

                        const where = mapWhereStringToFixedWhereOperator(whereStringTrimmed);

                        if (where === null) {
                            return null;
                        }

                        const filteredCoreDataPointModel = ahSelectedCoreDataPoints.find(dataPoint => dataPoint.id === Number(dataPointID));

                        if (!filteredCoreDataPointModel) {
                            return null;
                        }

                        const whereRequiresNoValue = whereStringTrimmed === 'ISNULL' || whereStringTrimmed === 'ISNOTNULL';

                        const whereUsesValueRHS = whereStringTrimmed === 'BETWEEN';

                        const valueLHSTrimmed = whereConfiguration.valueLHS.trim();

                        const valueRHSTrimmed = whereConfiguration.valueRHS.trim();

                        if (whereRequiresNoValue) {
                            return {
                                coreTypeID: filteredCoreDataPointModel.idType,
                                where: where,
                                valueLHS: null,
                                valueRHS: null,
                                fixedCoreDataPointModel: filteredCoreDataPointModel
                            };
                        }

                        if (valueLHSTrimmed.length === 0) {
                            return null;
                        }

                        if (whereUsesValueRHS && valueRHSTrimmed.length === 0) {
                            return null;
                        }

                        const normalizedBetweenWhereValues = whereUsesValueRHS
                            ? normalizeBetweenWhereValues(filteredCoreDataPointModel, valueLHSTrimmed, valueRHSTrimmed)
                            : null;

                        const whereData: AdHocReportFixedWhereModel = {
                            coreTypeID: filteredCoreDataPointModel.idType,
                            where: where,
                            valueLHS: normalizedBetweenWhereValues
                                ? normalizedBetweenWhereValues.valueLHS
                                : valueLHSTrimmed,
                            valueRHS: normalizedBetweenWhereValues
                                ? normalizedBetweenWhereValues.valueRHS
                                : null,
                            fixedCoreDataPointModel: filteredCoreDataPointModel
                        };

                        return whereData;
                    })
                    .filter((whereData): whereData is AdHocReportFixedWhereModel => whereData !== null);

                const adHocReportFixedWhereData: AdHocReportFixedWhereModel[] | null = adHocReportFixedWhereDataArray.length > 0
                    ?
                    adHocReportFixedWhereDataArray
                    :
                    null;
                
                const adHocReportVariableWhereDataArray: AdHocReportVariableWhereModel[] = ahDataPointVariableWheres
                    .filter(variableWhere =>
                        variableWhere.variableWhereCoreDataPointModel !== null
                    );

                const adHocReportVariableWhereData: AdHocReportVariableWhereModel[] | null = adHocReportVariableWhereDataArray.length > 0
                    ?
                    adHocReportVariableWhereDataArray
                    :
                    null;                

                const configuredWhereCount = Object.keys(ahDataPointFixedWheres).length;

                if (configuredWhereCount !== adHocReportFixedWhereDataArray.length) {
                    showAHReportErrorPopup(
                        'Ad Hoc Report Filter Validation Failed',
                        'One or more filter conditions are incomplete. Please review the configured where clauses and try again.'
                    );

                    return Promise.reject(new Error('INVALID_AH_REPORT_WHERE_CONFIGURATION'));
                }

                const requestData: AdHocReportDefinitionSetRequestModel = {
                    userID: user?.id ?? 0,
                    coreBrandID: ahSelectedCoreBrandID,
                    coreBrandName: ahSelectedCoreBrandName,
                    coreTenantID: ahSelectedCoreTenantID,
                    coreTenantName: ahSelectedCoreTenantName,
                    ahReportID: currentAHReportID,
                    ahReportGroupName: currentAHReportGroupName,
                    ahReportName: currentAHReportName,

                    coreBrandData: coreBrands.find((brand) => (brand.id === ahSelectedCoreBrandID)) ?? null,
                    coreTenantData: coreTenants.find((tenant) => (tenant.id === ahSelectedCoreTenantID)) ?? null,
                    coreTypeData: typesInDataPointsWithRoot.length > 0 ? typesInDataPointsWithRoot : null,
                    coreFieldData: fieldsInDataPointsWithRoot.length > 0 ? fieldsInDataPointsWithRoot : null,
                    coreDataPointData: ahSelectedCoreDataPoints.length > 0 ? ahSelectedCoreDataPoints : null,

                    coreTypeRootData: selectedCoreTypeRootData,

                    adHocReportFixedWhereData: adHocReportFixedWhereData,
                    adHocReportVariableWhereData: adHocReportVariableWhereData,
                    adHocReportOrderByData: adHocReportOrderByData,
                    adHocReportGroupByData: adHocReportGroupByData,
                    adHocReportFormatData: adHocReportFormatData,
                    adHocReportAggregateData: adHocReportAggregateData,

                    isAdd: false,
                    isDel: false
                };

                const responseData = await AxiosUtil.post<AdHocReportDefinitionSetResponseModel, AdHocReportDefinitionSetRequestModel>(url, requestData);

                return responseData;
            } catch (err) {
                const errorMessage = err instanceof Error ? err.message : String(err);

                setCurrentError('SET_AH_REPORT_REQUEST: [Set AH Report] Internal Error: ' + errorMessage);
                console.error('SET_AH_REPORT_REQUEST: [Set AH Report] Internal Error: ', err);

                return Promise.reject(err);
            }
        },
        {
            onError: (err: any) => {
                if (err instanceof Error && err.message === 'INVALID_AH_REPORT_WHERE_CONFIGURATION') {
                    return;
                }

                setCurrentError('SET_AH_REPORT_REQUEST: [Set AH Report] Internal Error: ' + err);
                console.error('SET_AH_REPORT_REQUEST: [Set AH Report] Internal Error: ', err);
                showAHReportErrorPopup(
                    'Ad Hoc Report Save Failed',
                    `Report Was Unable To Be ${getAHReportSubmitActionText()}. FAILED: Contact Application Support`
                );
            },
            onSuccess: (response: AdHocReportDefinitionSetResponseModel) => {
                showAHReportSuccessPopup(`AH Report: ${response.name} Was Successfully ${getAHReportSubmitActionText()}.`);
                invalidateReportMenu(queryClient);
                handleAHResetReportBuilder();
                handleAHReloadCoreData();
            },
        }
    );

    const { mutate: getAHReport, isLoading: isGetAHReportLoading, isError: isGetAHReportError, error: getGetAHReportError } = useMutation(
        async (reportID: number) => {
            try {
                const url = `${process.env.REACT_APP_API_ROOT}${Administration.ADHOC_REPORT_DEFINITION_GET_REQUEST}`;

                const requestData: AdHocReportDefinitionGetRequestModel = {
                    userID: user?.id ?? 0,
                    reportID: reportID,
                };

                const responseData = await AxiosUtil.post<AdHocReportDefinitionGetResponseModel, AdHocReportDefinitionGetRequestModel>(url, requestData);

                return responseData;
            } catch (err) {
                const errorMessage = err instanceof Error ? err.message : String(err);

                setCurrentError('GET_AH_REPORT_REQUEST: [Get AH Report] Internal Error: ' + errorMessage);
                console.error('GET_AH_REPORT_REQUEST: [Get AH Report] Internal Error: ', err);

                return Promise.reject(err);
            }
        },
        {
            onError: (err: any, reportID: number) => {
                setCurrentError('GET_AH_REPORT_REQUEST: [Get AH Report] Internal Error: ' + err);
                console.error('GET_AH_REPORT_REQUEST: [Get AH Report] Internal Error: ', err);
                showAHReportErrorPopup(
                    'Ad Hoc Report Load Failed',
                    `Report ${reportID} Was Unable To Be Loaded. FAILED: Contact Application Support`
                );
            },
            onSuccess: (response: AdHocReportDefinitionGetResponseModel, reportID: number) => {
                console.log(response);
                hydrateAdHocReportBuilder(response, reportID);
            },
        }
    );

    const onFinish = (values: any) => {
        handleAHAddReportConfirmCoreTypeRoot();
    }

    const handleAHSelectedReportSelectionChange = (reportID: number) => {
        if (!reportID || reportID === 0) {
            handleAHSelectedReportSelectionClear();
            return;
        }

        setAHSelectedReportID(reportID);

        getAHReport(reportID);
    };

    const handleAHSelectedReportSelectionClear = () => {
        setAHSelectedReportID(0);

        formAdHocReportBuilder.setFieldsValue({
            ahDetailsSelectedReport: 0,
            ahDetailsReportID: 0,
            ahDetailsReportName: undefined,
            ahDetailsReportGroupName: undefined
        });

        setAHSelectedCoreDataPoints([]);
        setAHDataPointCustomizations({});
        setAHDataPointAggregates({});
        setAHDataPointFixedWheres({});
        setAHSelectedOrderByDataPointID(undefined);

        ahSelectedCoreTypeRootDataRef.current = undefined;

        setAHReportBuilderSessionKey(currentKey => currentKey + 1);
    };

    const handleAHCoreBrandSelectionChange = (coreBrandID: number) => {
        if (!coreBrandID || coreBrandID === 0) {
            handleAHResetReportBuilder();
            return;
        }

        const selectedBrand = coreBrands.find((brand) => brand.id === coreBrandID);

        setAHSelectedCoreBrandID(coreBrandID);
        setAHSelectedCoreBrandName(selectedBrand?.name ?? '');

        const selectedTenant = coreTenants.find((tenant) => tenant.id === selectedBrand?.idTenant);

        setAHSelectedCoreTenantID(selectedTenant?.id ?? 0);
        setAHSelectedCoreTenantName(selectedTenant?.name ?? '');

        const selectedTypes = coreTypes.filter(coreType => coreType.idBrand === selectedBrand?.id);

        setAHSelectedCoreTypes(selectedTypes);

        formAdHocReportBuilder.setFieldsValue({
            ahDetailsTenantName: selectedTenant?.name ?? '',
            ahDetailsSelectedReport: 0,
            ahDetailsReportID: 0,
            ahDetailsReportName: undefined,
            ahDetailsReportGroupName: undefined
        });

        setAHSelectedCoreDataPoints([]);

        setAHDataPointCustomizations({});
        setAHDataPointAggregates({});
        setAHDataPointFixedWheres({});
        setAHSelectedOrderByDataPointID(undefined);

        ahSelectedCoreTypeRootDataRef.current = undefined;

        setAHExistingReports([]);
        setAHSelectedReportID(0);

        setAHReportBuilderSessionKey(currentKey => currentKey + 1);

        getAHReportList(coreBrandID);
    };

    const handleSetSelectedCoreDataPoints = (coreDataPoints: CoreDataPoint[]) => {
        setAHSelectedCoreDataPoints(coreDataPoints);
    }

    const handleSetDataPointCustomizations = (dataPointCustomizations: AHRBDataPointCustomizationMap) => {
        setAHDataPointCustomizations(dataPointCustomizations);
    }

    const handleSetDataPointAggregates = (dataPointAggregates: AHRBDataPointAggregateMap) => {
        setAHDataPointAggregates(dataPointAggregates);
    }

    const handleSetDataPointFixedWheres = (dataPointFixedWheres: AHRBDataPointFixedWhereMap) => {
        setAHDataPointFixedWheres(dataPointFixedWheres);
    }

    const handleSetDataPointVariableWheres = (dataPointVariableWheres: AdHocReportVariableWhereModel[]) => {
        setAHDataPointVariableWheres(dataPointVariableWheres);
    };

    const handleSetSelectedOrderByDataPointID = (selectedOrderByDataPointID: number | undefined) => {
        setAHSelectedOrderByDataPointID(selectedOrderByDataPointID);
    }

    const getAHReportSubmitButtonText = () => {
        return ahSelectedReportID > 0 ? 'Update Report' : 'Add Report';
    };

    const showAHReportSuccessPopup = (message: React.ReactNode) => {
        ServaModal.success({
            title: 'Ad Hoc Report Builder',
            content: message,
            okText: 'OK'
        });
    };

    const showAHReportErrorPopup = (title: string, message: React.ReactNode) => {
        ServaModal.error({
            title: title,
            content: message,
            okText: 'OK'
        });
    };

    //=================================================================
    //=================================================================
    
    const handleAHAddReportConfirmCoreTypeRoot = () => {
        setAHPendingReportGroupName(
            formAdHocReportBuilder.getFieldValue('ahDetailsReportGroupName') ?? ''
        );

        setAHPendingReportName(
            formAdHocReportBuilder.getFieldValue('ahDetailsReportName') ?? ''
        );

        setAHReportCoreTypeRootDataOpen(true);
    }

    const handleAHAddReportConfirmedCoreTypeRoot = (selectedCoreTypeRoot: CoreType) => {
        setAHReportCoreTypeRootDataOpen(false);

        ahSelectedCoreTypeRootDataRef.current = selectedCoreTypeRoot;
        
        setAHReport();

        setCurrentError('');
    };

    //=================================================================

    const handleAHResetReportBuilder = () => {
        formAdHocReportBuilder.resetFields();

        formAdHocReportBuilder.setFieldsValue({
            ahDetailsCoreBrand: 0,
            ahDetailsTenantName: '',
            ahDetailsSelectedReport: 0,
            ahDetailsReportID: 0,
            ahDetailsReportName: undefined,
            ahDetailsReportGroupName: undefined,
        });

        setAHSelectedCoreTenantID(0);
        setAHSelectedCoreTenantName('');
        setAHSelectedCoreBrandID(0);
        setAHSelectedCoreBrandName('');

        setAHExistingReports([]);
        setAHSelectedReportID(0);

        setAHSelectedCoreTypes([]);
        setAHSelectedCoreDataPoints([]);
        setAHDataPointCustomizations({});
        setAHDataPointAggregates({});
        setAHDataPointFixedWheres({});
        setAHSelectedOrderByDataPointID(undefined);

        setAHPendingReportGroupName('');
        setAHPendingReportName('');
        setAHReportCoreTypeRootDataOpen(false);

        ahSelectedCoreTypeRootDataRef.current = undefined;

        setAHReportBuilderSessionKey(currentKey => currentKey + 1);

        setCurrentError('');
    };

    const handleAHReloadCoreData = () => {
        getCoreBrands(false);
        getCoreTenants(false);
        getCoreTypes(false);
        getCoreFields(false);
    };

    const ahTypeIdsInDataPoints = new Set(ahSelectedCoreDataPoints.map(dataPoint => dataPoint.idType));
    const ahTypesInDataPoints = ahSelectedCoreTypes.filter(types => ahTypeIdsInDataPoints.has(types.id));
    const initialAHSelectedCoreTypeRootId = ahTypesInDataPoints[0] ? ahTypesInDataPoints[0].id : undefined;
    
    const contentStyle: React.CSSProperties = {
        lineHeight: '95%',
        textAlign: 'center',
        color: 'black',
        backgroundColor: 'azure',
        borderRadius: '10',
        border: `1px solid black`,
        marginTop: 8,
        height: '95%'
    };

    const determineWhichError = () => {
        if ( isGetCoreBrandsError ) {
            return currentError + ' ' + ((getCoreBrandsError as any)?.response?.data?.message ?? '');
        }
        else if ( isGetCoreTenantsError ) {
            return currentError + ' ' + ((getCoreTenantsError as any)?.response?.data?.message ?? '');
        }
        else if ( isGetCoreTypesError ) {
            return currentError + ' ' + ((getCoreTypesError as any)?.response?.data?.message ?? '');
        }
        else if ( isGetCoreFieldsError ) {
            return currentError + ' ' + ((getCoreFieldsError as any)?.response?.data?.message ?? '');
        }
        else if ( isSetAHReportError ) {
            return currentError + ' ' + ((getSetAHReportError as any)?.response?.data?.message ?? '');
        }
        else if ( isGetAHReportError ) {
            return currentError + ' ' + ((getGetAHReportError as any)?.response?.data?.message ?? '');
        }
        else if ( isGetAHReportListError ) {
            return currentError + ' ' + ((getGetAHReportListError as any)?.response?.data?.message ?? '');
        }
        else {
            return '';
        }
    }    

    return (
        <Card className='shadow-card admin-full-page-card' size='small'>
            <ServaModal
                destroyOnHidden={true}
                hideButtons={true}
                open={ahReportCoreTypeRootDataOpen}
                width='35%'
            >
                {ahReportCoreTypeRootDataOpen && (
                    <AHRBCoreTypeRootSelection
                        key={`${ahSelectedCoreBrandID}-${ahTypesInDataPoints.map(x => x.id).join('-')}`}
                        ahReportGroupName={ahPendingReportGroupName}
                        ahReportName={ahPendingReportName}
                        typesInDataPoints={ahTypesInDataPoints}
                        initialSelectedCoreTypeRootId={initialAHSelectedCoreTypeRootId}
                        handleSetCoreTypeRootSelection={(selectedCoreTypeRoot) => {
                            ahSelectedCoreTypeRootDataRef.current = selectedCoreTypeRoot;
                        }}
                        onCancel={() => setAHReportCoreTypeRootDataOpen(false)}
                        onOk={handleAHAddReportConfirmedCoreTypeRoot}
                    />
                )}
            </ServaModal>
            <Spin spinning={
                            isGetCoreBrandsLoading ||
                            isGetCoreTenantsLoading ||
                            isGetCoreTypesLoading ||
                            isGetCoreFieldsLoading ||
                            isSetAHReportLoading ||
                            isGetAHReportLoading ||
                            isGetAHReportListLoading
                           }>
                <Space direction="horizontal" style={{justifyContent: 'center', padding: '4px', width: '100%'}}>
                    <Text style={{height: '100%', width: '100%'}}>
                        <Title style={{backgroundColor: 'rgb(00, 55, 90)', borderRadius: '10px', color: 'white', height: '100%', padding: '8px', width: '100%'}} level={4}>Ad Hoc Report Builder</Title>
                    </Text>
                </Space>
                <div style={{ display: 'table', margin: 'auto', textAlign: 'center', width: '50%' }}>
                {
                    (
                        isGetCoreBrandsError ||
                        isGetCoreTenantsError ||
                        isGetCoreTypesError ||
                        isGetCoreFieldsError ||
                        isSetAHReportError ||
                        isGetAHReportError ||
                        isGetAHReportListError
                    )
                    &&
                    (
                        <ServaAlert
                            className={'mb-2'}
                            message={determineWhichError()}
                            showIcon={true}
                            type='error'
                        ></ServaAlert>
                    )
                }
                </div>
                <Row gutter={16} className='retainbi-compact-row'>
                    <Col span={1} />
                    <Col span={22}>
                        <div>
                            <Divider style={{ borderTop: '1px solid black', margin: '1px' }}></Divider>
                        </div>
                    </Col>
                    <Col span={1} />
                </Row>
                <Form
                    form={formAdHocReportBuilder}
                    key='ahReportBuilder'
                    onFinish={(values: any) => onFinish(values)}
                    onFinishFailed={(error) => console.log(error)}
                >
                    <Row gutter={16} className='retainbi-compact-row'>
                        <Col span={1} />
                        <Col span={3} style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                            <Text italic style={{fontWeight: 'bold'}}>Select a Brand to begin:</Text>
                        </Col>
                        <Col span={9}>
                            <Form.Item
                                name='ahDetailsCoreBrand'
                                initialValue={0}
                                style={{ marginTop: '8px', marginBottom: '0px' }}
                            >
                                <Select
                                    styles={{ popup: { root: { width: 500 } } }}
                                    filterOption={(input, option) => (option?.label as string ?? '').toLowerCase().includes(input.toLowerCase())}
                                    placeholder={'Select a Brand'}
                                    showSearch
                                    style={{ width: '100%' }}
                                    onChange={(coreBrandID: number) => handleAHCoreBrandSelectionChange(coreBrandID)}
                                >
                                    <Option key={0} value={0} label='(Not Set)'>
                                        (Not Set)
                                    </Option>

                                    {coreBrands.map((eachBrand) => {
                                        return (
                                            <Option
                                                key={eachBrand.id}
                                                value={eachBrand.id}
                                                label={eachBrand.name}
                                            >
                                                {eachBrand.name}
                                            </Option>
                                        );
                                    })}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={3} style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                            <Text italic>Associated Tenant:</Text>
                        </Col>
                        <Col span={6}>
                            <Form.Item
                                key='ahdetailstenantname'
                                name='ahDetailsTenantName'
                                style={{marginBottom: '0px'}}
                            >
                                <Input
                                    readOnly
                                />
                            </Form.Item>
                        </Col>
                        <Col span={1} />
                    </Row>
                    <Row gutter={16} className='retainbi-compact-row'>
                        <Col span={1} />
                        <Col span={22}>
                            <div>
                                <Divider style={{ borderTop: '1px solid black', margin: '1px' }}></Divider>
                            </div>
                        </Col>
                        <Col span={1} />
                    </Row>
                    <Row gutter={16} className='retainbi-compact-row'>
                        <Col span={1} />
                        <Col span={3} style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                            Selected Report:
                        </Col>
                        <Col span={18}>
                            <Form.Item
                                initialValue={0}
                                key='ahdetailsselectedreport'
                                name='ahDetailsSelectedReport'
                                rules={[
                                    {
                                        message: 'Selected Report -or- Report Name and Report Group Name required.',
                                        validator: validateAHDetailsData,
                                    },
                                ]}
                                style={{marginTop: '8px', marginBottom: '0px'}}
                                validateTrigger='onSubmit'
                            >
                                <Select
                                    allowClear
                                    disabled={ahSelectedCoreBrandID === 0}
                                    styles={{ popup: { root: { width: 500 } } }}
                                    filterOption={(input, option) => (option?.label as string ?? '').toLowerCase().includes(input.toLowerCase())}
                                    placeholder='Select Report'
                                    showSearch
                                    style={{width: '100%'}}
                                    onChange={(reportID: number) => handleAHSelectedReportSelectionChange(reportID)}
                                    onClear={handleAHSelectedReportSelectionClear}
                                >
                                    <Option
                                        key={0}
                                        value={0}
                                        label='[New Report]'
                                    >
                                        [New Report]
                                    </Option>

                                    {ahExistingReports.map(
                                        (eachReport) => {
                                            return (
                                                <Option
                                                    key={eachReport.ahReportID}
                                                    value={eachReport.ahReportID}
                                                    label={`[${eachReport.ahReportGroupName}] ${eachReport.ahReportName}`}
                                                >
                                                    [{eachReport.ahReportGroupName}] {eachReport.ahReportName}
                                                </Option>
                                            );
                                        }
                                    )}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={2} />
                    </Row>
                    <Row gutter={16} className='retainbi-compact-row'>
                        <Col span={1} />
                        <Col span={22}>
                            <div>
                                <Divider style={{ borderTop: '1px solid lightgray', margin: '1px' }}></Divider>
                            </div>
                        </Col>
                        <Col span={1} />
                    </Row>
                    <Row gutter={16} className='retainbi-compact-row'>
                        <Col span={1} />
                        <Col span={3} style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                            Report Name:
                        </Col>
                        <Col span={6}>
                            <Form.Item
                                key='ahdetailsreportname'
                                name='ahDetailsReportName'
                                rules={[
                                    {
                                        message: 'Report Name required for a New Report.',
                                        validator: validateAHDetailsData,
                                    },
                                ]}
                                style={{marginBottom: '4px'}}
                                validateTrigger='onSubmit'
                            >
                                <Input
                                    placeholder="Enter Report Name"
                                />
                            </Form.Item>
                        </Col>
                        <Col span={3} style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                            Report Group Name:
                        </Col>
                        <Col span={6}>
                            <Form.Item
                                key='ahdetailsreportgroupname'
                                name='ahDetailsReportGroupName'
                                rules={[
                                    {
                                        message: 'Report Group Name required for a New Report.',
                                        validator: validateAHDetailsData,
                                    },
                                ]}
                                style={{marginBottom: '4px'}}
                                validateTrigger='onSubmit'
                            >
                                <Input
                                    placeholder="Enter Report Group Name"
                                />
                            </Form.Item>
                        </Col>
                        <Col span={2} style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                            <Text italic>Report ID:</Text>
                        </Col>
                        <Col span={2}>
                            <Form.Item
                                initialValue={0}
                                key='ahdetailsreportid'
                                name='ahDetailsReportID'
                                style={{marginBottom: '0px'}}
                            >
                                <Input
                                    readOnly
                                />
                            </Form.Item>
                        </Col>
                        <Col span={1} />
                    </Row>
                    <Row gutter={16} style={{ padding: '16px', paddingBottom: '2px', paddingTop: '2px', minHeight: '570px' }}>
                        <Col span={24} >
                            {
                                <div style={contentStyle}>
                                    <AHRBuilderSelections
                                        key={ahReportBuilderSessionKey}
                                        coreTypes={ahSelectedCoreTypes}
                                        coreFields={coreFields}
                                        brandID={ahSelectedCoreBrandID}
                                        initialSelectedCoreDataPoints={ahSelectedCoreDataPoints}
                                        initialSelectedOrderByDataPointID={ahSelectedOrderByDataPointID}
                                        initialDataPointCustomizations={ahDataPointCustomizations}
                                        initialDataPointAggregates={ahDataPointAggregates}
                                        initialDataPointFixedWheres={ahDataPointFixedWheres}
                                        initialDataPointVariableWheres={ahDataPointVariableWheres}
                                        handleSetSelectedCoreDataPoints={handleSetSelectedCoreDataPoints}
                                        handleSetSelectedOrderByDataPointID={handleSetSelectedOrderByDataPointID}
                                        handleSetDataPointCustomizations={handleSetDataPointCustomizations}
                                        handleSetDataPointAggregates={handleSetDataPointAggregates}
                                        handleSetDataPointFixedWheres={handleSetDataPointFixedWheres}
                                        handleSetDataPointVariableWheres={handleSetDataPointVariableWheres}
                                    ></AHRBuilderSelections>
                                </div>
                            }
                        </Col>
                    </Row>
                    <Row gutter={16} className='retainbi-compact-row'>
                        <Col span={16} />
                        <Col span={4}>
                            <div style={{ textAlign: 'right' }}>
                                <Form.Item
                                    name="rAddReportButton"
                                    style={{ marginBottom: '0px' }}
                                >
                                    <Button
                                        disabled={ahSelectedCoreDataPoints.length === 0}
                                        htmlType="submit"
                                        style={{width: '95%', backgroundColor: 'cornflowerblue'}}
                                        type='primary'
                                    >
                                        {getAHReportSubmitButtonText()}
                                    </Button>
                                </Form.Item>
                            </div>
                        </Col>
                    </Row>
                </Form>
            </Spin>
        </Card>
    );
};

export default AdHocReportBuilder;
